import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { LifecycleBandView } from "@/types/dashboard/genie-console";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface LayoutProps {
  bands: LifecycleBandView[];
}

/**
 * Direction C — "Filmstrip".
 *
 * Dense and horizontal. Each stage keeps a fixed summary column on the left
 * (big tinted numeral, label, live state line, one link) and the user's own
 * content runs off to the right inside its own scroller. Nothing is more than
 * one screen tall, so the whole lifecycle is visible at once on desktop and
 * swipes sideways on mobile.
 */
export const FilmstripLayout = ({ bands }: LayoutProps) => (
  <div className="space-y-4">
    {bands.map((band, index) => (
      <section
        key={band.id}
        className="overflow-hidden rounded-[20px] border border-genie-border bg-white shadow-sm"
      >
        <div className="flex flex-col md:flex-row">
          {/* Fixed summary rail */}
          <div
            className="flex w-full flex-shrink-0 flex-col justify-between gap-3 px-5 py-5 md:w-[280px]"
            style={{ backgroundColor: `${band.tint}0f` }}
          >
            <div>
              <div className="flex items-center gap-2.5">
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${band.tint}22` }}
                >
                  <GenieIcon name={band.genieIcon} size={18} color={band.tint} />
                </span>
                <span
                  className="text-[26px] font-extrabold leading-none tracking-[-1px]"
                  style={{ color: `${band.tint}66` }}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>
              <h2
                className="mt-3 text-[15px] font-extrabold tracking-[2.5px]"
                style={{ color: band.tint }}
              >
                {band.label}
              </h2>
              <p className="mt-1 text-sm font-semibold text-[#222B45]">{band.tagline}</p>
              <p className="mt-2 text-sm text-gray-600">{band.stateLine}</p>
            </div>

            <Link
              to={band.linkTo}
              className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
              style={{ color: band.tint }}
            >
              {band.linkLabel}
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </Link>
          </div>

          {/* Live content strip */}
          <div className="min-w-0 flex-1 overflow-x-auto border-t border-[#F3F4F6] px-5 py-5 md:border-l md:border-t-0">
            <div className="min-w-[280px] md:min-w-[520px]">{band.preview}</div>
          </div>
        </div>
      </section>
    ))}
  </div>
);

export default FilmstripLayout;
